<script setup lang="ts">
import { computed } from 'vue'
import { useTableStore } from '@/stores/tableStore.ts'
import { useGameStore } from '@/stores/gameStore.ts'
import FerionDataTable from '@/components/Interface/DataTable/FerionDataTable.vue'
import ActionBarFiller from '@/components/Interface/DataTable/ActionBar/ActionBarFiller.vue'
import ActionBarSearch from '@/components/Interface/DataTable/ActionBar/ActionBarSearch.vue'
import HeaderTextFlex from '@/components/Interface/DataTable/Headers/HeaderTextFlex.vue'
import HeaderFixedWidthText from '@/components/Interface/DataTable/Headers/HeaderFixedWidthText.vue'
import CellFixedWidth from '@/components/Interface/DataTable/Cell/CellFixedWidth.vue'
import CellFlexWidth from '@/components/Interface/DataTable/Cell/CellFlexWidth.vue'
import { useMapStore } from '@/stores/mapStore.ts'
import { useRouteStore } from '@/stores/routeStore.ts'
import { useRouteEditorStore } from '@/stores/routeEditorStore.ts'
import type Route from '@/types/Route.ts'
import ActionBarBlock from '@/components/Interface/DataTable/ActionBar/ActionBarBlock.vue'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import Backend from '@/models/backend.ts'
import GenericIcon from '@/components/Elements/Images/Icons/GenericIcon.vue'
import HeaderFixedWidthTextNoSort from '@/components/Interface/DataTable/Headers/HeaderFixedWidthTextNoSort.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const tableStore = useTableStore()
const routeStore = useRouteStore()
const routeEditorStore = useRouteEditorStore()
const mapStore = useMapStore()
const gameStore = useGameStore()

const hasRoutes = computed(() => routeStore.routes.size > 0)

const filters = (routes: Route[]) => {
  return routes.filter((route) => {
    if (!tableStore.routes.filters.nameSearch || tableStore.routes.filters.nameSearch === '') {
      return true
    }
    return route.name.toLowerCase().includes(tableStore.routes.filters.nameSearch.toLowerCase())
  })
}

const onClick = (id: number) => {
  const route = routeStore.findRouteById(id)
  if (route && route.waypoints.length > 0) {
    // Focus on first waypoint
    mapStore.focusMap(route.waypoints[0].x, route.waypoints[0].y)
  }
}

const onDoubleClick = (id: number) => {
  routeEditorStore.startEditRoute(id, true)
}

const onAddRoute = () => {
  mapStore.mapSideBarVisible = false
  routeEditorStore.startNewRoute()
}

const onEditRoute = (id: number, event: Event) => {
  event.stopPropagation()
  routeEditorStore.startEditRoute(id, true)
}

const onDeleteRoute = (id: number, event: Event) => {
  event.stopPropagation()
  const route = routeStore.findRouteById(id)
  if (!route) return

  gameStore.modal2Type = 'confirmationDelete'
  gameStore.modal2Data = {
    title: t('map.sidebar.routes.deleteConfirm.title'),
    message: t('map.sidebar.routes.deleteConfirm.message', { name: route.name }),
    actionText: t('map.sidebar.routes.deleteConfirm.action'),
    cancelText: t('map.sidebar.routes.deleteConfirm.cancel'),
    action: async () => {
      try {
        const backend = new Backend()
        await backend.deleteRoute(id)
        gameStore.setTinySuccessToastMessage(t('notifications.success.routeDeleted'))
      } catch (error: any) {
        gameStore.setTinyErrorToastMessage(error.response?.data?.message || t('map.sidebar.routes.errors.deleteFailed'))
      }
    }
  }
}
</script>

<template>
  <!-- Empty state when no routes exist -->
  <div v-if="!hasRoutes" class="p-6 h-[calc(100vh-5.3rem)] flex flex-col justify-center text-center bg-[radial-gradient(circle_at_center,rgba(50,50,55,0.3)_0%,transparent_70%)]">
    <div class="text-lg font-semibold mb-4">{{ t('map.sidebar.routes.title') }}</div>
    <div class="text-subtext mb-6 text-sm leading-relaxed">
      <p class="mb-3">{{ t('map.sidebar.routes.description') }}</p>
      <p>{{ t('map.sidebar.routes.patrolDefend') }}</p>
    </div>
    <div>
      <ActionButton @click="onAddRoute">{{ t('map.route.createFirst') }}</ActionButton>
    </div>
  </div>

  <!-- Route list when routes exist -->
  <FerionDataTable
    v-else
    tableStoreName="routes"
    :selector="false"
    :filters="filters"
    :heightLimit="'h-[calc(100vh-5.3rem)]'"
    :data="routeStore.routes"
    :click="onClick"
    :double-click="onDoubleClick"
  >
    <!--- ActionBar --->
    <template #actionBar>
      <ActionBarBlock class="mr-1 pl-2 flex items-center rounded">
        <ActionButton @click="onAddRoute" class="my-1">{{ t('map.route.newRoute') }}</ActionButton>
      </ActionBarBlock>
      <ActionBarFiller class="mr-1 rounded">
        <div class="py-3">&nbsp;</div>
      </ActionBarFiller>
      <ActionBarSearch
        v-model="tableStore.routes.filters.nameSearch"
        :name="t('map.sidebar.routes.name')"
        :placeHolder="t('map.sidebar.routes.search')"
      />
    </template>
    <!--- Headers --->
    <template #columns="{ sort, sortColumn, sortDirection }">
      <HeaderTextFlex
        min-width="12"
        column="name"
        :sortColumn="sortColumn"
        :sortDirection="sortDirection"
        @sort="sort"
        class="rounded-tl pl-2"
      >
        {{ t('map.sidebar.routes.name') }}
      </HeaderTextFlex>
      <HeaderFixedWidthText
        width="5"
        align="center"
        column="waypoints"
        :sortColumn="sortColumn"
        :sortDirection="sortDirection"
        @sort="sort"
      >
        {{ t('map.sidebar.routes.waypoints') }}
      </HeaderFixedWidthText>
      <HeaderFixedWidthTextNoSort width="6" align="center" class="rounded-tr">
        {{ t('map.sidebar.routes.action') }}
      </HeaderFixedWidthTextNoSort>
    </template>
    <!--- Rows --->
    <template #rows="{ item }">
      <CellFlexWidth min-width="12" class="pl-2">
        <div class="flex-col">
          <div>{{ item.name }}</div>
        </div>
      </CellFlexWidth>
      <CellFixedWidth width="5" align="center">
        <span class="text-subtext">{{ item.waypoints.length }}</span>
      </CellFixedWidth>
      <CellFixedWidth width="6" align="center">
        <div class="flex justify-center">
          <ActionButton @click="onEditRoute(item.id, $event)" :for-icon="true" class="mr-1">
            <GenericIcon type="edit" :tooltip="t('map.sidebar.routes.editRoute')"/>
          </ActionButton>
          <ActionButton @click="onDeleteRoute(item.id, $event)" :for-icon="true">
            <GenericIcon type="trash" :tooltip="t('map.sidebar.routes.deleteRoute')"/>
          </ActionButton>
        </div>
      </CellFixedWidth>
    </template>
  </FerionDataTable>
</template>

<style scoped lang="scss"></style>